import {useEffect, useRef, useState} from 'react';

import {API_CONFIG} from '../contentGenerator/config';
import {DEFAULT_CUSTOM_COMMANDS} from '../contentReview/constants';
import {apiFetch} from './apiFetch';

const SETTINGS_URL = `${API_CONFIG.PROXY_URL}/review-ai-settings`;

const cloneDefaultCustomCommands = () =>
    DEFAULT_CUSTOM_COMMANDS.map((command) => ({...command}));

const normalizeCustomCommands = (commands, fallbackToDefaults = false) => {
    if (!Array.isArray(commands)) {
        return fallbackToDefaults ? cloneDefaultCustomCommands() : [];
    }

    return commands
        .map((command) => {
            const id = typeof command?.id === 'string' ? command.id.trim() : '';
            const label = typeof command?.label === 'string' ? command.label.trim() : '';
            const prompt = typeof command?.prompt === 'string' ? command.prompt.trim() : '';
            const colorId = typeof command?.colorId === 'string' ? command.colorId.trim() : '';

            if (!id || !label || !prompt) return null;

            return {
                id,
                label,
                prompt,
                colorId: colorId || 'slate',
            };
        })
        .filter(Boolean);
};

const normalizeCustomActions = (actions) =>
    Array.isArray(actions)
        ? Array.from(
              new Set(
                  actions
                      .map((action) =>
                          typeof action === 'string' ? action.trim() : '',
                      )
                      .filter(Boolean),
              ),
          )
        : [];

const createDefaultSettings = () => ({
    customCommands: cloneDefaultCustomCommands(),
    customActions: [],
});

const normalizeSettings = (settings) => {
    if (!settings || typeof settings !== 'object') {
        return createDefaultSettings();
    }

    return {
        customCommands: normalizeCustomCommands(settings.customCommands, true),
        customActions: normalizeCustomActions(settings.customActions),
    };
};

async function parseApiResponse(response, fallbackMessage) {
    const contentType = response.headers.get('content-type') || '';
    const payload = contentType.includes('application/json')
        ? await response.json().catch(() => null)
        : await response.text().catch(() => '');

    if (!response.ok) {
        throw new Error(
            payload?.error ||
                payload?.message ||
                (typeof payload === 'string' && payload) ||
                fallbackMessage,
        );
    }

    return payload;
}

export async function loadReviewAiSettings() {
    if (typeof fetch !== 'function') return null;

    try {
        const response = await apiFetch(SETTINGS_URL);
        const payload = await parseApiResponse(
            response,
            'Could not load the review AI settings.',
        );
        return payload ? normalizeSettings(payload) : null;
    } catch {
        return null;
    }
}

export async function saveReviewAiSettings(settings) {
    if (typeof fetch !== 'function') return normalizeSettings(settings);

    const payload = normalizeSettings(settings);
    const response = await apiFetch(SETTINGS_URL, {
        method: 'PUT',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify(payload),
    });

    const saved = await parseApiResponse(
        response,
        'Could not save the review AI settings.',
    );

    return normalizeSettings(saved);
}

export function useReviewAiSettings() {
    const [settings, setSettings] = useState(createDefaultSettings);
    const lastSavedRef = useRef(JSON.stringify(createDefaultSettings()));
    const hydratedRef = useRef(false);
    const saveTimeoutRef = useRef(null);

    useEffect(() => {
        let active = true;

        (async () => {
            const loadedSettings = await loadReviewAiSettings();
            if (!active) return;

            const nextSettings = loadedSettings || createDefaultSettings();
            lastSavedRef.current = JSON.stringify(nextSettings);
            hydratedRef.current = true;
            setSettings(nextSettings);
        })();

        return () => {
            active = false;
            if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        };
    }, []);

    useEffect(() => {
        if (!hydratedRef.current) return undefined;

        const serializedSettings = JSON.stringify(settings);
        if (serializedSettings === lastSavedRef.current) return undefined;

        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);

        saveTimeoutRef.current = setTimeout(async () => {
            try {
                const savedSettings = await saveReviewAiSettings(settings);
                lastSavedRef.current = JSON.stringify(savedSettings);
            } catch (error) {
                console.warn('Could not sync review AI settings:', error.message);
            }
        }, 250);

        return () => {
            if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        };
    }, [settings]);

    const setCustomCommands = (nextValue) => {
        setSettings((current) => ({
            ...current,
            customCommands: normalizeCustomCommands(
                typeof nextValue === 'function'
                    ? nextValue(current.customCommands)
                    : nextValue,
                false,
            ),
        }));
    };

    const setCustomActions = (nextValue) => {
        setSettings((current) => ({
            ...current,
            customActions: normalizeCustomActions(
                typeof nextValue === 'function'
                    ? nextValue(current.customActions)
                    : nextValue,
            ),
        }));
    };

    return {
        customActions: settings.customActions,
        customCommands: settings.customCommands,
        setCustomActions,
        setCustomCommands,
    };
}
